<template>
    <div class="skagen-consent" v-if="showConsent">
        <GeneralConsent
            v-if="showGeneralConsent"
            :blockViewMode="blockViewMode"
            :consentTexts="consentTexts"
            @consent:showDetails="showDetails"
            @consent:rejectAll="rejectAll"
            @consent:acceptAll="acceptAll"
        >
        </GeneralConsent>
        <DetailsConsent
            v-if="showDetailConsent"
            :blockViewMode="blockViewMode"
            :consentTexts="consentTexts"
            :consentData="consentData"
            @consent:showGeneral="showGeneral"
            @consent:acceptCustom="(input) => acceptCustom(input)"
        >
        </DetailsConsent>
    </div>
</template>
<script>
import GeneralConsent from './GeneralConsent';
import DetailsConsent from './DetailsConsent';
import { getConsent, saveConsent, isConsentGiven } from './consentUtil';

import {
    trackTradidionalGaEvent,
    EventInfoCategory,
    EventInfoAction,
} from '../components/GoogleTagManager';
import { getConsentTexts } from '@/api/getConsentTexts';

export default {
    name: 'ConsentMain',
    components: {
        GeneralConsent,
        DetailsConsent,
    },
    props: {
        blockViewMode: { type: Boolean },
    },
    data() {
        return {
            fetchError: false,
            initComplete: false,
            showConsent: false,
            showGeneralConsent: false,
            showDetailConsent: false,
            consentTexts: {},
            consentData: {
                basic: true,
                useful: false,
                analytics: false,
                marketing: false,
            },
        };
    },
    created() {
        this.fetchConsentData();
    },
    methods: {
        setupConsent() {
            if (this.fetchError) {
                // If we fail to fetch consent data, we do not want to show the consent.
                return;
            }
            // We do not want to show disclaimer on Simple mode (from CIP and APP)
            const queryString = window.location.search;
            if (queryString && queryString.includes('Simple')) {
                return;
            }

            if (isConsentGiven()) {
                this.consentData = getConsent();
            }

            // page inline block mode
            if (this.blockViewMode) {
                trackTradidionalGaEvent(
                    EventInfoCategory.Consent,
                    EventInfoAction.None,
                    'DisplayConcentPage'
                );
                this.showGeneralConsent = false;
                this.showDetailConsent = true;
                this.showConsent = true;
            }
            // 1. We use else to prevent both to display at the same page.
            // 2. Displays on all pages: Check if GDPR Consent cookie exists. Will show consent inline if no cookie.
            else if (!isConsentGiven()) {
                trackTradidionalGaEvent(
                    EventInfoCategory.Consent,
                    EventInfoAction.None,
                    'DisplayConcent'
                );
                this.showGeneralConsent = true;
                this.showConsent = true;
                // Prevent scrolling on the page when consent is visible
                document.querySelector('html').classList.add('noScroll');
            }
            this.initComplete = true;
        },
        async fetchConsentData() {
            try {
                this.consentTexts = await getConsentTexts();
            } catch (err) {
                this.fetchError = true;
            } finally {
                this.setupConsent();
            }
        },
        showDetails() {
            trackTradidionalGaEvent(
                EventInfoCategory.Consent,
                EventInfoAction.Button,
                'showDetails'
            );

            this.showGeneralConsent = false;
            this.showDetailConsent = true;
        },
        showGeneral() {
            trackTradidionalGaEvent(
                EventInfoCategory.Consent,
                EventInfoAction.Button,
                'backToGeneralConsent'
            );
            this.showDetailConsent = false;
            this.showGeneralConsent = true;
        },
        acceptAll() {
            this.consentData.basic = true;
            this.consentData.useful = true;
            this.consentData.analytics = true;
            this.consentData.marketing = true;

            saveConsent(this.consentData);
            trackTradidionalGaEvent(
                EventInfoCategory.Consent,
                EventInfoAction.Button,
                'acceptAll'
            );
            this.showConsent = false;
            document.querySelector('html').classList.remove('noScroll');
        },
        rejectAll() {
            this.consentData.basic = true;
            this.consentData.useful = false;
            this.consentData.analytics = false;
            this.consentData.marketing = false;

            saveConsent(this.consentData);
            trackTradidionalGaEvent(
                EventInfoCategory.Consent,
                EventInfoAction.Button,
                'rejectAll'
            );
            this.showConsent = false;
            document.querySelector('html').classList.remove('noScroll');
        },
        acceptCustom(customConsent) {
            saveConsent(customConsent);
            trackTradidionalGaEvent(
                EventInfoCategory.Consent,
                EventInfoAction.Button,
                'acceptCustom'
            );
            if (!this.blockViewMode) {
                this.showConsent = false;
                document.querySelector('html').classList.remove('noScroll');
            }
        },
    },
};
</script>
<style>
.noScroll,
.noScroll body {
    overflow: hidden !important;
    position: relative;
    height: 100%;
}

.skagen-consent h2 {
    margin-top: 0;
    @media (max-width: 480px) {
        margin-bottom: 15px;
        font-size: 24px;
    }
}
.skagen-consent .text--gdpr {
    margin-bottom: 20px;

    @media (max-width: 480px) {
        font-size: 16px;
    }
}

.skagen-consent a {
    color: #333;
    text-decoration: underline;
}

.skagen-consent p {
    @media (max-width: 480px) {
        margin-bottom: 30px;
    }
}

@media (max-width: 480px) {
    .skagen-consent .btn {
        margin-bottom: 15px;
    }
}

.skagen-consent .skagen-consent-overlay {
    z-index: 10001;
    -webkit-backdrop-filter: blur(15px);
    backdrop-filter: blur(10px);
    background-color: var(--color-scrim);
    width: 100%;
    height: 100%;
    position: fixed;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    @media (max-width: 480px) {
        display: block;
        bottom: 0;
    }
}

.skagen-consent-overlay .skagen-consent-container {
    z-index: 1;
    max-height: 100%;
    overflow: auto;
    padding: 20px 30px;
    background: #fff;

    @media (max-width: 480px) {
        margin: 0;
        height: 100%;
    }
}
</style>
@/api/getConsentTexts
